import React from 'react';
import type {
  Plan,
  PricingPlansSectionOptions,
} from '@wix/app-market-components';
import type { CurrencySettings, TaxDetails } from '@wix/app-market-services';
import { PricingPlansSection as PricingPlansSectionComponent } from '@wix/app-market-components';
import {
  useEssentials,
  useUserContext,
  useSiteContext,
  getAppMarketAppPageClickBiData,
  getAppMarketPackagePickerToggleCycleClickBiData,
} from '@wix/app-market-core';
import {
  appMarketAppPageClick,
  appmarketPackagePickerToggleCycleClick,
} from '@wix/bi-logger-app-market-data/v2';
import type { AppPageType } from '@wix/app-market-core-components-types';

export interface AppPricingPlansSectionProps {
  externalPricingPageUrl?: string;
  isExternalPricing?: boolean;
  trialDays?: number;
  tax: TaxDetails;
  currencySettings: CurrencySettings;
  companyName: string;
  appName: string;
  plans: Plan[];
  appId: string;
  options?: PricingPlansSectionOptions;
  appPageType: AppPageType;
}
export function AppPricingPlansSection({
  externalPricingPageUrl,
  isExternalPricing,
  trialDays,
  tax,
  currencySettings,
  companyName,
  appName,
  plans,
  appId,
  options,
  appPageType,
}: AppPricingPlansSectionProps) {
  const { biLogger } = useEssentials();
  const userContext = useUserContext();
  const { languageCode, metaSiteId, isPremium } = useSiteContext();
  const { i18n, experiments } = useEssentials();

  const useNewQuickbooksPackagePickerUrl = experiments.enabled(
    'specs.premium.QuickBooksPurchaseFlowBm',
  );
  const isMobile = userContext?.isMobile ?? false;
  const sectionName = 'pricing';

  const getComparePlansUrl = () => {
    const QUICKBOOKS_APP_ID = '2374b1f8-39ba-4d4c-bfd3-2fdec89ee4c4';

    if (
      useNewQuickbooksPackagePickerUrl &&
      appId === QUICKBOOKS_APP_ID &&
      metaSiteId &&
      isPremium
    ) {
      return `https://manage.wix.com/dashboard/${metaSiteId}/premium-quickbooks-bm/package-picker`;
    }
    return externalPricingPageUrl;
  };

  const compareAppPlansUrl = getComparePlansUrl();

  const onToggleClick = (toggleValue: string) => {
    biLogger.report(
      appmarketPackagePickerToggleCycleClick(
        getAppMarketPackagePickerToggleCycleClickBiData({
          appId,
          cycleClick: toggleValue,
          numberOfPlans: plans.length,
          appPageType,
        }),
      ),
    );
  };

  const onLinkClick = () => {
    biLogger.report(
      appMarketAppPageClick(
        getAppMarketAppPageClickBiData({
          appId,
          location: sectionName,
          cta: 'compare_all_plans',
          url: compareAppPlansUrl ?? '',
          appPageType,
        }),
      ),
    );
  };

  return (
    <PricingPlansSectionComponent
      compareAppPlansUrl={compareAppPlansUrl}
      isExternalPricing={isExternalPricing}
      trialDays={trialDays}
      tax={tax}
      currencySettings={currencySettings}
      companyName={companyName}
      appName={appName}
      plans={plans}
      isMobile={isMobile}
      i18n={i18n}
      experiments={experiments}
      onToggleClick={onToggleClick}
      onLinkClick={onLinkClick}
      options={options}
      languageCode={languageCode}
    />
  );
}
